import * as LabelPrimitive from '@radix-ui/react-label';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Form controls.
 *
 * `min-h-10` and `text-base` on mobile are both load-bearing: iOS Safari zooms
 * the whole page when a focused input's font is under 16px, which on a
 * merchant's phone reads as the dashboard jumping around every time they type.
 */
const fieldClasses =
  'w-full rounded-md border border-input bg-card px-3 py-2 text-base md:text-sm min-h-10 ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export function Input({ className, type, ...props }: ComponentProps<'input'>) {
  return <input type={type} data-slot="input" className={cn(fieldClasses, className)} {...props} />;
}

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      data-slot="textarea"
      className={cn(fieldClasses, 'min-h-16 py-2', className)}
      {...props}
    />
  );
}

/**
 * A native `<select>` rather than Radix's.
 *
 * Radix Select is a popover the page has to render and position; the platform
 * one is a wheel the OS renders natively. On the mid-range Android §7 budgets
 * for, the native control is faster, reachable one-handed, and already
 * translated — none of which a custom listbox gives back.
 */
export function Select({ className, ...props }: ComponentProps<'select'>) {
  return <select data-slot="select" className={cn(fieldClasses, 'pr-8', className)} {...props} />;
}

export function Label({
  className,
  ...props
}: ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn(
        'mb-1.5 block text-[0.78rem] font-semibold text-muted-foreground',
        'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className,
      )}
      {...props}
    />
  );
}
